import { createAsyncThunk, isFulfilled, isPending } from '@reduxjs/toolkit';
import axios from 'axios';

import { IDeviceCommunication, defaultValue } from 'app/shared/model/device-communication.model';
import { EntityState, IQueryParams, createEntitySlice, serializeAxiosError } from 'app/shared/reducers/reducer.utils';
import { cleanEntity } from 'app/shared/util/entity-utils';

const initialState: EntityState<IDeviceCommunication> = {
  loading: false,
  errorMessage: null,
  entities: [],
  countAlt: null,
  entity: defaultValue,
  updating: false,
  totalItems: 0,
  updateSuccess: false,
};

const apiUrl = 'api/device-communications';

// Actions

interface IDeviceCommunicationParams extends IQueryParams {
  companySearch?: string;
  assetSearch?: string;
}

export const getEntities = createAsyncThunk(
  'deviceCommunication/fetch_entity_list',
  async ({ page, size, sort, companySearch, assetSearch }: IDeviceCommunicationParams) => {
    const companySearchRequest = companySearch ? `companySearch=${companySearch}&` : '';
    const assetSearchRequest = assetSearch ? `assetSearch=${assetSearch}&` : '';

    const requestUrl = `${apiUrl}${
      sort ? `?page=${page}&size=${size}&sort=${sort}&${companySearchRequest}${assetSearchRequest}` : '?'
    }cacheBuster=${new Date().getTime()}`;
    return axios.get<IDeviceCommunication[]>(requestUrl);
  }
);

export const getEntity = createAsyncThunk(
  'deviceCommunication/fetch_entity',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/${id}`;
    return axios.get<IDeviceCommunication>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const createEntity = createAsyncThunk(
  'deviceCommunication/create_entity',
  async (entity: IDeviceCommunication, thunkAPI) => {
    const result = await axios.post<IDeviceCommunication>(apiUrl, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const updateEntity = createAsyncThunk(
  'deviceCommunication/update_entity',
  async (entity: IDeviceCommunication, thunkAPI) => {
    const result = await axios.put<IDeviceCommunication>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const partialUpdateEntity = createAsyncThunk(
  'deviceCommunication/partial_update_entity',
  async (entity: IDeviceCommunication, thunkAPI) => {
    const result = await axios.patch<IDeviceCommunication>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const deleteEntity = createAsyncThunk(
  'deviceCommunication/delete_entity',
  async (id: string | number, thunkAPI) => {
    const requestUrl = `${apiUrl}/${id}`;
    const result = await axios.delete<IDeviceCommunication>(requestUrl);
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const getDelayedDeviceCommunicationsCount = createAsyncThunk(
  'deviceCommunication/fetch_delayed_inspections_count',
  async () => {
    const requestUrl = `${apiUrl}/delayed/count`;
    return axios.get<number>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

// slice

export const DeviceCommunicationSlice = createEntitySlice({
  name: 'deviceCommunication',
  initialState,
  extraReducers(builder) {
    builder
      .addCase(getEntity.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(deleteEntity.fulfilled, state => {
        state.updating = false;
        state.updateSuccess = true;
        state.entity = {};
      })
      .addMatcher(isFulfilled(getEntities), (state, action) => {
        return {
          ...state,
          loading: false,
          entities: action.payload.data,
          totalItems: parseInt(action.payload.headers['x-total-count'], 10),
        };
      })
      .addMatcher(isFulfilled(getDelayedDeviceCommunicationsCount), (state, action) => {
        return {
          ...state,
          countAlt: action.payload.data,
        };
      })
      .addMatcher(isFulfilled(createEntity, updateEntity, partialUpdateEntity), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.entity = action.payload.data;
      })
      .addMatcher(isPending(getEntities, getEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
      })
      .addMatcher(isPending(createEntity, updateEntity, partialUpdateEntity, deleteEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      });
  },
});

export const { reset } = DeviceCommunicationSlice.actions;

// Reducer
export default DeviceCommunicationSlice.reducer;
